import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Button } from '../components/atoms/Button/Button';
import { EmptyState } from '../components/atoms/EmptyState';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { Text } from '../components/atoms/Typography/Text';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { CommunityFeedItem } from '../components/community/CommunityFeedItem';
import { CommunityFeedFilters } from '../components/community/CommunityFeedFilters';
import {
  countActiveFeedFilters,
  toApiFilters,
  useCommunityFeedFilters,
} from '../components/community/useCommunityFeedFilters';
import { COMMUNITY_FEED_QUERY_KEY } from '../components/community/JournalEntrySocial';
import { ActiveAquariumContext } from '../contexts/AquariumContext';
import { getCommunityFeed } from '../services/communityService';
import { useTranslation } from '../i18n/useTranslation';
import './CommunityPage.css';

const PAGE_SIZE = 10;

/**
 * /community — public journal entries of all users (audit #11), with their
 * public photos and server-side filters that live in the URL (shareable,
 * survive reload). No person filter by design: the feed never shows names or
 * e-mail addresses.
 */
export function CommunityPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeAquariumId = useContext(ActiveAquariumContext)?.activeAquariumId ?? null;
  const { state: filterState, update, setPage, reset } = useCommunityFeedFilters();
  const page = filterState.page;
  const apiFilters = useMemo(() => toApiFilters(filterState), [filterState]);
  const hasActiveFilters = countActiveFeedFilters(filterState) > 0;

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: [...COMMUNITY_FEED_QUERY_KEY, page, apiFilters],
    queryFn: () => getCommunityFeed(page, PAGE_SIZE, apiFilters),
    placeholderData: keepPreviousData,
  });

  const journalPath = activeAquariumId ? `/aquariums/${activeAquariumId}/journal` : '/aquariums';
  const entries = data?.entries ?? [];
  const totalPages = data?.totalPages ?? 0;

  let body;
  if (isLoading) {
    body = (
      <LiveRegion data-testid="community-loading">
        <Text color="muted">{t('community.loading')}</Text>
      </LiveRegion>
    );
  } else if (isError) {
    body = (
      <LoadErrorState
        data-testid="community-error"
        title={t('community.loadError')}
        onRetry={() => void refetch()}
      />
    );
  } else if (entries.length === 0 && hasActiveFilters) {
    body = (
      <div data-testid="community-empty-filtered">
        <EmptyState
          icon={<span>🔍</span>}
          headline={t('community.filters.emptyTitle')}
          description={t('community.filters.emptyDescription')}
          actionLabel={t('community.filters.reset')}
          actionVariant="primary"
          onAction={reset}
        />
      </div>
    );
  } else if (entries.length === 0) {
    body = (
      <EmptyState
        icon={<span>🌊</span>}
        headline={t('feed.empty')}
        description={t('community.emptyDescription')}
        actionLabel={t('community.emptyAction')}
        actionVariant="secondary"
        onAction={() => navigate(journalPath)}
      />
    );
  } else {
    body = (
      <>
        <ul className="community-page__list" data-testid="community-feed-list" aria-busy={isFetching}>
          {entries.map((entry) => (
            <li key={entry.id}>
              <CommunityFeedItem entry={entry} />
            </li>
          ))}
        </ul>
        {totalPages > 1 && (
          <nav className="community-page__pagination" aria-label={t('community.pagination')}>
            <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              {t('journal.previous')}
            </Button>
            <Text as="span" size="sm" color="secondary">
              {t('journal.pageOf', { page, total: totalPages })}
            </Text>
            <Button variant="ghost" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
              {t('journal.next')}
            </Button>
          </nav>
        )}
      </>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default" data-testid="community-page">
          <PageHeader
            title={t('feed.title')}
            description={t('feed.subtitle')}
            actions={
              <Button variant="primary" onClick={() => navigate(journalPath)}>
                {t('community.shareAction')}
              </Button>
            }
          />
          <Text color="secondary">{t('community.privacyNote')}</Text>
          <CommunityFeedFilters value={filterState} onChange={update} onReset={reset} />
          {/* Announces the filtered result count ("12 entries found"). */}
          <LiveRegion className="community-page__result-count" busy={isFetching} data-testid="community-result-count">
            {hasActiveFilters && data && !isError && !isLoading && (
              <Text as="span" size="sm" color="secondary">
                {t('community.filters.resultCount', { count: data.totalCount })}
              </Text>
            )}
          </LiveRegion>
          {body}
        </PageContent>
      }
    />
  );
}

export default CommunityPage;
